/**
 * FOUNDING-1 / FOUNDING-3 — reads and writes for a Founding Member's page
 * (src/pages/FoundingKit.tsx).
 *
 * Opening the page needs no sign-in: the member's private token IS the
 * credential for reading it and holding kit picks. Three SECURITY
 * DEFINER RPCs granted to `anon`, each keyed on that exact token (SQL:
 * supabase/migrations/…_founding1_kit.sql and …_founding3_pass.sql):
 *
 *   founding_kit_get(p_token)            → jsonb | null
 *   founding_kit_claim(p_token, p_claim) → jsonb   (hold or change picks)
 *   founding_pass_begin(p_token)         → jsonb   (see beginFoundingPass)
 *
 * A token that matches nobody reads as `null` — the page says the link
 * isn't working rather than anything that would confirm a guess.
 *
 * Buying a Pass is the one step that needs an Orbit account, because
 * the Pass is an Orbit subscription: `startFoundingPass` calls the
 * founding-pass-checkout edge function with the signed-in session and
 * the Pass the member chose (Orbit or Orbit Business).
 *
 * `/founding/demo` never touches Supabase: a sample member whose picks
 * live in memory for the tab. `?state=` previews every state the page
 * has.
 */
import { supabase } from "@/integrations/supabase/client";
import { readFunctionErrorBody, type SubscriptionPlan } from "@/services/billing";
import {
  DEFAULT_PASS_TIER,
  DEFAULT_SHIP_COUNTRY,
  HOODIE_SIZES,
  isPassTier,
  KIT_PICKS_CLOSE_FALLBACK,
  type HoodieSize,
  type PassTier,
} from "@/config/founding";

/**
 * open      nothing picked yet
 * reserved  picked, Pass not bought — the picks are held, not an order
 * claimed   picked and the Pass is paid — an order; editable until close
 * locked    picks closed (or Rennan locked them for production)
 * shipped   on its way; `trackingUrl` may be set
 */
export type KitStatus = "open" | "reserved" | "claimed" | "locked" | "shipped";

/** This member's Pass, once it's paid. */
export interface FoundingPassInfo {
  number: number;
  paidAt: string;
  /** Which Pass: Orbit or Orbit Business. */
  tier: PassTier;
}

/** The Pass as it stands for everyone: how many are left, until when. */
export interface PassAvailability {
  cap: number;
  left: number;
  closesAt: string;
  /** Not closed and not sold out. */
  open: boolean;
}

export interface KitPicks {
  hoodie: string;
  size: HoodieSize;
  pin: string;
  patch: string;
}

export interface ShipTo {
  name: string;
  line1: string;
  line2: string;
  city: string;
  region: string;
  postal: string;
  /** ISO 3166-1 alpha-2. */
  country: string;
  email: string;
  phone: string;
}

export interface FoundingKit {
  firstName: string | null;
  name: string | null;
  email: string | null;
  status: KitStatus;
  /** ISO timestamp. After it, picks can't change. */
  picksCloseAt: string;
  picks: KitPicks | null;
  shipTo: ShipTo | null;
  claimedAt: string | null;
  trackingUrl: string | null;
  /** Their Pass, once paid. The kit is an order only then. */
  pass: FoundingPassInfo | null;
  /** Null until the FOUNDING-3 migration is applied. */
  passes: PassAvailability | null;
}

export type KitErrorCode =
  | "invalid_token"
  | "picks_closed"
  | "invalid_claim"
  | "pass_closed"
  | "sold_out"
  | "unavailable";

export class FoundingKitError extends Error {
  code: KitErrorCode;
  constructor(code: KitErrorCode, message?: string) {
    super(message ?? code);
    this.code = code;
    this.name = "FoundingKitError";
  }
}

/** What the RPCs return (snake_case, straight from Postgres). */
interface KitRow {
  first_name: string | null;
  name: string | null;
  email: string | null;
  status: KitStatus | null;
  picks_close_at: string | null;
  claim: null | {
    hoodie: string;
    hoodie_size: string;
    pin: string;
    patch: string;
    ship_name: string;
    ship_line1: string;
    ship_line2: string | null;
    ship_city: string;
    ship_region: string | null;
    ship_postal: string | null;
    ship_country: string;
    ship_email: string;
    ship_phone: string | null;
    claimed_at: string | null;
    tracking_url: string | null;
  };
  pass?: null | { number: number; paid_at: string; tier?: string | null };
  passes?: null | { cap: number; left: number; closes_at: string; open: boolean };
}

function asSize(v: string): HoodieSize {
  return (HOODIE_SIZES as readonly string[]).includes(v) ? (v as HoodieSize) : "M";
}

function fromRow(row: KitRow): FoundingKit {
  const c = row.claim;
  return {
    firstName: row.first_name,
    name: row.name,
    email: row.email,
    status: row.status ?? (c ? "reserved" : "open"),
    picksCloseAt: row.picks_close_at ?? KIT_PICKS_CLOSE_FALLBACK,
    picks: c ? { hoodie: c.hoodie, size: asSize(c.hoodie_size), pin: c.pin, patch: c.patch } : null,
    shipTo: c
      ? {
          name: c.ship_name,
          line1: c.ship_line1,
          line2: c.ship_line2 ?? "",
          city: c.ship_city,
          region: c.ship_region ?? "",
          postal: c.ship_postal ?? "",
          country: c.ship_country,
          email: c.ship_email,
          phone: c.ship_phone ?? "",
        }
      : null,
    claimedAt: c?.claimed_at ?? null,
    trackingUrl: c?.tracking_url ?? null,
    pass: row.pass
      ? {
          number: row.pass.number,
          paidAt: row.pass.paid_at,
          tier: isPassTier(row.pass.tier) ? row.pass.tier : DEFAULT_PASS_TIER,
        }
      : null,
    passes: row.passes
      ? {
          cap: Number(row.passes.cap),
          left: Number(row.passes.left),
          closesAt: row.passes.closes_at,
          open: !!row.passes.open,
        }
      : null,
  };
}

/** The claim as the RPC takes it — every value trimmed. */
function toClaim(picks: KitPicks, ship: ShipTo) {
  const t = (s: string) => s.trim();
  return {
    hoodie: picks.hoodie,
    hoodie_size: picks.size,
    pin: picks.pin,
    patch: picks.patch,
    ship_name: t(ship.name),
    ship_line1: t(ship.line1),
    ship_line2: t(ship.line2),
    ship_city: t(ship.city),
    ship_region: t(ship.region),
    ship_postal: t(ship.postal),
    ship_country: ship.country,
    ship_email: t(ship.email),
    ship_phone: t(ship.phone),
  };
}

function classify(error: { message?: string; code?: string } | null): FoundingKitError {
  const m = error?.message ?? "";
  if (m.includes("invalid_token")) return new FoundingKitError("invalid_token");
  if (m.includes("picks_closed")) return new FoundingKitError("picks_closed");
  if (m.includes("invalid_claim")) return new FoundingKitError("invalid_claim", m);
  if (m.includes("pass_closed")) return new FoundingKitError("pass_closed");
  if (m.includes("sold_out")) return new FoundingKitError("sold_out");
  return new FoundingKitError("unavailable", m || "Couldn't reach Orbit.");
}

// The two RPCs aren't in the generated Database types until Lovable
// applies the migration, so they're called through a loose handle —
// the same pattern every other token page uses.
type LooseRpc = (
  fn: string,
  args: Record<string, unknown>,
) => Promise<{ data: unknown; error: { message?: string; code?: string } | null }>;
const rpc: LooseRpc = (fn, args) =>
  (supabase as unknown as { rpc: LooseRpc }).rpc(fn, args);

/** The member behind `token`, or null when the link matches nobody. */
export async function getFoundingKit(token: string): Promise<FoundingKit | null> {
  const { data, error } = await rpc("founding_kit_get", { p_token: token });
  if (error) {
    // Until the migration is applied the function doesn't exist
    // (PGRST202). To a member that's the same as a link that doesn't
    // work — the page must not look broken.
    if (error.code === "PGRST202") return null;
    throw classify(error);
  }
  return data ? fromRow(data as KitRow) : null;
}

/** Save (or change) the member's picks. Returns the updated kit. */
export async function claimFoundingKit(token: string, picks: KitPicks, ship: ShipTo): Promise<FoundingKit> {
  const { data, error } = await rpc("founding_kit_claim", {
    p_token: token,
    p_claim: toClaim(picks, ship),
  });
  if (error) throw classify(error);
  if (!data) throw new FoundingKitError("invalid_token");
  return fromRow(data as KitRow);
}

// ── Buying the Pass ─────────────────────────────────────────────────────

/**
 * "Get the Founding Pass" from a member who isn't signed in. The Pass
 * goes on an Orbit account, and while the waitlist gate is up a new
 * account needs an invited waitlist entry — pressing the button on your
 * own founding page is that invitation. Returns whether the member's
 * email already has an account (→ sign in) or the invite token for
 * /auth?invite=… (→ create one).
 */
export async function beginFoundingPass(token: string): Promise<{
  email: string;
  name: string | null;
  hasAccount: boolean;
  inviteToken: string | null;
}> {
  const { data, error } = await rpc("founding_pass_begin", { p_token: token });
  if (error) throw classify(error);
  const d = (data ?? {}) as { email?: string; name?: string | null; has_account?: boolean; invite_token?: string | null };
  return {
    email: d.email ?? "",
    name: d.name ?? null,
    hasAccount: !!d.has_account,
    inviteToken: d.invite_token ?? null,
  };
}

/** What switching an existing Orbit subscription to the Pass costs today. */
export interface PlanChangePreview {
  /** The Pass it would switch to. */
  tier: PassTier;
  currentPlan: Exclude<SubscriptionPlan, "free">;
  currentInterval: "month" | "year" | null;
  trialing: boolean;
  amountDueCents: number;
  passCents: number;
  creditCents: number;
  currency: string;
}

export type PassCheckoutResult =
  | { kind: "redirect"; url: string }
  | { kind: "planChange"; preview: PlanChangePreview }
  | { kind: "done"; passNumber: number; tier: PassTier };

export type PassCheckoutErrorCode =
  | "not_signed_in"
  | "invalid_token"
  | "no_org"
  | "pass_closed"
  | "sold_out"
  | "member_has_pass"
  | "org_has_pass"
  | "apple_subscription_active"
  | "billing_issue"
  | "payment_failed"
  | "nothing_to_switch"
  | "unavailable";

export class PassCheckoutError extends Error {
  code: PassCheckoutErrorCode;
  passNumber: number | null;
  constructor(code: PassCheckoutErrorCode, message: string, passNumber: number | null = null) {
    super(message);
    this.code = code;
    this.passNumber = passNumber;
    this.name = "PassCheckoutError";
  }
}

const PASS_ERROR_CODES: PassCheckoutErrorCode[] = [
  "not_signed_in",
  "invalid_token",
  "no_org",
  "pass_closed",
  "sold_out",
  "member_has_pass",
  "org_has_pass",
  "apple_subscription_active",
  "billing_issue",
  "payment_failed",
  "nothing_to_switch",
];

/**
 * Buy a Pass for the signed-in account (founding-pass-checkout).
 * First call: a Stripe Checkout URL, or — for a studio already paying
 * for Orbit — a preview of switching that subscription. Call again with
 * `confirm: true` to switch.
 */
export async function startFoundingPass(
  token: string,
  tier: PassTier,
  opts: { confirm?: boolean; returnPath?: string } = {},
): Promise<PassCheckoutResult> {
  const { data, error } = await supabase.functions.invoke("founding-pass-checkout", {
    body: { token, tier, confirm: !!opts.confirm, returnPath: opts.returnPath },
  });
  if (error) {
    const body = (await readFunctionErrorBody(error)) as
      | ({ code?: string; message?: string; passNumber?: number } & Record<string, unknown>)
      | null;
    const code = (PASS_ERROR_CODES as string[]).includes(body?.code ?? "")
      ? (body?.code as PassCheckoutErrorCode)
      : "unavailable";
    throw new PassCheckoutError(
      code,
      body?.message || (typeof body?.error === "string" ? (body.error as string) : "") || "Checkout didn't open.",
      typeof body?.passNumber === "number" ? body.passNumber : null,
    );
  }
  const d = (data ?? {}) as {
    url?: string;
    planChange?: PlanChangePreview;
    done?: boolean;
    passNumber?: number;
    tier?: string;
  };
  if (d.url) return { kind: "redirect", url: d.url };
  if (d.planChange) {
    return { kind: "planChange", preview: { ...d.planChange, tier: isPassTier(d.planChange.tier) ? d.planChange.tier : tier } };
  }
  if (d.done && typeof d.passNumber === "number") {
    return { kind: "done", passNumber: d.passNumber, tier: isPassTier(d.tier) ? d.tier : tier };
  }
  throw new PassCheckoutError("unavailable", "Checkout didn't open.");
}

// ── Picking up where they left off ─────────────────────────────────────
//
// A member without an account leaves their page for /auth and then the
// sign-up wizard; a member who is signed out leaves for /auth and comes
// back. Their token — and the Pass they chose — ride along in
// localStorage (not sessionStorage — an email-confirmation link can open
// a new tab), so the wizard's plan step can offer that Pass and the page
// can carry on after sign-in.

const INTENT_KEY = "orbit_founding_intent";
const INTENT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface FoundingIntent {
  token: string;
  tier: PassTier;
}

export function saveFoundingIntent(token: string, tier: PassTier): void {
  try {
    window.localStorage.setItem(INTENT_KEY, JSON.stringify({ token, tier, at: Date.now() }));
  } catch {
    /* private mode — the flow still works, the wizard just won't offer the Pass */
  }
}

export function readFoundingIntent(): FoundingIntent | null {
  try {
    const raw = window.localStorage.getItem(INTENT_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { token?: unknown; tier?: unknown; at?: unknown };
    if (typeof v.token !== "string" || typeof v.at !== "number") return null;
    if (Date.now() - v.at > INTENT_TTL_MS) {
      window.localStorage.removeItem(INTENT_KEY);
      return null;
    }
    return { token: v.token, tier: isPassTier(v.tier) ? v.tier : DEFAULT_PASS_TIER };
  } catch {
    return null;
  }
}

export function clearFoundingIntent(): void {
  try {
    window.localStorage.removeItem(INTENT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * FOUNDERS-GO-4 — the same intent, as the setup wizard saves it on the
 * account (`founding_token` / `founding_tier` in the user's metadata),
 * so it follows the founder to another browser. localStorage wins when
 * both exist; this is the fallback.
 */
export function foundingIntentOf(
  user: { user_metadata?: Record<string, unknown> | null } | null | undefined,
): FoundingIntent | null {
  const meta = user?.user_metadata ?? null;
  const token = meta && typeof meta.founding_token === "string" ? meta.founding_token : null;
  if (!token || token.length < 16) return null;
  return { token, tier: isPassTier(meta?.founding_tier) ? (meta?.founding_tier as PassTier) : DEFAULT_PASS_TIER };
}

// ── Demo ────────────────────────────────────────────────────────────────

export type DemoState =
  | "open"
  | "reserved"
  | "paid"
  | "paidbusiness"
  | "locked"
  | "shipped"
  | "soldout"
  | "closed"
  | "invalid";

const DEMO_PICKS: KitPicks = {
  hoodie: "hoodie-h9",
  size: "M",
  pin: "pin-orbit-ring",
  patch: "patch-orbit-insignia",
};

const DEMO_SHIP: ShipTo = {
  name: "Jordan Ellis",
  line1: "1600 Larimer St",
  line2: "Apt 4B",
  city: "Denver",
  region: "CO",
  postal: "80202",
  country: DEFAULT_SHIP_COUNTRY,
  email: "jordan@example.com",
  phone: "",
};

const DEMO_PASSES: PassAvailability = {
  cap: 50,
  left: 37,
  closesAt: KIT_PICKS_CLOSE_FALLBACK,
  open: true,
};

function demoBase(): FoundingKit {
  return {
    firstName: "Jordan",
    name: "Jordan Ellis",
    email: "jordan@example.com",
    status: "open",
    picksCloseAt: KIT_PICKS_CLOSE_FALLBACK,
    picks: null,
    shipTo: null,
    claimedAt: null,
    trackingUrl: null,
    pass: null,
    passes: DEMO_PASSES,
  };
}

/** The demo member for this tab (nothing leaves the browser). */
let demoSaved: { picks: KitPicks; ship: ShipTo; at: string } | null = null;
let demoPaid: FoundingPassInfo | null = null;

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function getDemoKit(state: string | null): Promise<FoundingKit | null> {
  await wait(250);
  const base = demoBase();
  const picked = { picks: DEMO_PICKS, shipTo: DEMO_SHIP, claimedAt: "2026-10-02T17:12:00Z" };
  const paid: FoundingPassInfo = { number: 7, paidAt: "2026-10-02T17:14:00Z", tier: "orbit" };
  switch (state as DemoState | null) {
    case "invalid":
      return null;
    case "reserved":
      return { ...base, ...picked, status: "reserved" };
    case "paid":
      return { ...base, ...picked, status: "claimed", pass: paid, passes: { ...DEMO_PASSES, left: 36 } };
    case "paidbusiness":
      return {
        ...base,
        ...picked,
        status: "claimed",
        pass: { ...paid, number: 12, tier: "business" },
        passes: { ...DEMO_PASSES, left: 36 },
      };
    case "locked":
      return { ...base, ...picked, status: "locked", pass: paid };
    case "shipped":
      return {
        ...base,
        ...picked,
        status: "shipped",
        pass: paid,
        trackingUrl: "https://tools.usps.com/go/TrackConfirmAction",
      };
    case "soldout":
      return { ...base, passes: { ...DEMO_PASSES, left: 0, open: false } };
    case "closed":
      return { ...base, status: "locked", passes: { ...DEMO_PASSES, open: false } };
    default: {
      const kit: FoundingKit = demoSaved
        ? { ...base, status: "reserved", picks: demoSaved.picks, shipTo: demoSaved.ship, claimedAt: demoSaved.at }
        : base;
      return demoPaid ? { ...kit, status: kit.picks ? "claimed" : "open", pass: demoPaid } : kit;
    }
  }
}

export async function claimDemoKit(picks: KitPicks, ship: ShipTo): Promise<FoundingKit> {
  await wait(600);
  demoSaved = { picks, ship, at: new Date().toISOString() };
  return {
    ...demoBase(),
    status: demoPaid ? "claimed" : "reserved",
    picks,
    shipTo: ship,
    claimedAt: demoSaved.at,
    pass: demoPaid,
  };
}

/** The demo's "Get the Founding Pass": no Stripe, just the paid state. */
export async function buyDemoPass(tier: PassTier): Promise<FoundingKit> {
  await wait(900);
  demoPaid = { number: 7, paidAt: new Date().toISOString(), tier };
  const kit = await getDemoKit(null);
  return kit as FoundingKit;
}
